import React, { createContext, useState, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { get } from "../utils/get";

interface AuthState {
  access_token: string | null;
  refresh_token: string | null;
  user: Record<string, any>;
}

interface AuthContextProps {
  authState: AuthState;
  setAuthState: (authInfo: AuthState) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  isManager: () => boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);
const { Provider } = AuthContext;

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  const access_token = localStorage.getItem("access_token");
  const user = localStorage.getItem("user");
  const refresh_token = localStorage.getItem("refresh_token");

  const [authState, setAuthState] = useState<AuthState>({
    access_token,
    refresh_token,
    user: user ? JSON.parse(user) : {},
  });

  const setAuthInfo = ({ access_token, user, refresh_token }: AuthState) => {
    localStorage.setItem("access_token", access_token || "");
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("refresh_token", refresh_token || "");
    setAuthState({ access_token, user, refresh_token });
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("refresh_token");
    setAuthState({ access_token: null, refresh_token: null, user: {} });
    history.push("/login");
  };

  const isAuthenticated = () => !!authState.access_token && !!authState.refresh_token;

  const isAdmin = () => {
    return get(authState, "user.role.name") === "ADMIN" || get(authState, "user.role.name") === "MANAGER";
  };

  const isManager = () => {
    return !!get(authState, "user.is_manager");
  };

  return (
    <Provider
      value={{
        authState,
        setAuthState: setAuthInfo,
        logout,
        isAuthenticated,
        isAdmin,
        isManager,
      }}
    >
      {children}
    </Provider>
  );
};

export { AuthContext, AuthProvider };